import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import api, { customers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, User, Search, MapPin, Edit, Trash2, History, Download, FileSpreadsheet, FileText, Navigation, RefreshCw, Phone, Mail, X } from 'lucide-react';
import { toast } from 'sonner';

const STATES = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

export function CustomersPage() {
  const [storeCoords, setStoreCoords] = useState({ lat: -23.5314103, lng: -46.7980872 });
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('TODOS');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showAddressesModal, setShowAddressesModal] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  
  const [formData, setFormData] = useState({
    person_type: 'FISICA',
    name: '',
    cpf: '',
    rg: '',
    birth_date: '',
    phone: '',
    email: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    delivery_fee: '',
    notes: '',
    lat: null,
    lng: null,
  });

  useEffect(() => {
    api.get('/store/location').then(r => {
      setStoreCoords({ lat: r.data.latitude, lng: r.data.longitude });
    }).catch(e => console.error('Erro ao buscar coordenadas:', e));
  }, []);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const response = await customers.list(params);
      setCustomersList(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(() => {
    setLoading(true);
    loadCustomers();
  }, [searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearch();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const geocodeAddress = async (street, number, city, state) => {
    if (!street || !city) return null;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (street) params.set('street', street);
      if (number) params.set('number', number);
      if (city) params.set('city', city);
      if (state) params.set('state', state);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/geocode?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.ok) return null;
      const data = await response.json();
      if (typeof data?.lat === 'number' && typeof data?.lng === 'number') {
        return { lat: data.lat, lng: data.lng };
      }
    } catch (e) { console.error('Geocode error:', e); }
    return null;
  };

  const calculateFeeFromCoords = (lat, lng) => {
    // Usa storeCoords do state (fonte: API /store/location)
    const R = 6371;
    const dLat = (lat - storeCoords.lat) * Math.PI / 180;
    const dLon = (lng - storeCoords.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(storeCoords.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLon/2)**2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    const fee = Math.max(5, distance * 2).toFixed(2);
    return { distance, fee };
  };

  const handleCepSearch = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos');
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      const newStreet = data.logradouro;
      const newCity = data.localidade;
      const newState = data.uf;
      const newNeighborhood = data.bairro;

      setFormData(prev => ({
        ...prev,
        street: newStreet,
        neighborhood: newNeighborhood,
        city: newCity,
        state: newState,
      }));
      toast.success('Endereço encontrado!');

      // Auto-geocodificar após buscar CEP
      const coords = await geocodeAddress(newStreet, formData.number || '1', newCity, newState);
      if (coords) {
        const { distance, fee } = calculateFeeFromCoords(coords.lat, coords.lng);
        setFormData(prev => ({
          ...prev,
          lat: coords.lat,
          lng: coords.lng,
          delivery_fee: prev.delivery_fee || fee,
        }));
        toast.success(`Coordenadas: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)} | Dist: ${distance.toFixed(1)}km | Taxa: R$ ${fee}`);
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    }
  };

  const handleUpdateCoordinates = async () => {
    if (!formData.street || !formData.city) {
      toast.error('Preencha o endereço primeiro');
      return;
    }

    const coords = await geocodeAddress(formData.street, formData.number || '1', formData.city, formData.state);
    if (coords) {
      const { distance, fee } = calculateFeeFromCoords(coords.lat, coords.lng);
      setFormData(prev => ({
        ...prev,
        lat: coords.lat,
        lng: coords.lng,
        delivery_fee: prev.delivery_fee || fee,
      }));
      toast.success(`Coordenadas: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)} | Dist: ${distance.toFixed(1)}km | Taxa: R$ ${fee}`);
    } else {
      toast.error('Não foi possível encontrar as coordenadas');
    }
  };

  const handleCalculateDistance = async () => {
    if (!formData.lat || !formData.lng) {
      toast.error('Atualize as coordenadas primeiro');
      return;
    }

    const { distance, fee } = calculateFeeFromCoords(formData.lat, formData.lng);
    setFormData(prev => ({ ...prev, delivery_fee: fee }));
    toast.success(`Distância: ${distance.toFixed(2)} km | Taxa sugerida: R$ ${fee}`);
  };

  const resetForm = () => {
    setFormData({
      person_type: 'FISICA',
      name: '',
      cpf: '',
      rg: '',
      birth_date: '',
      phone: '',
      email: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      delivery_fee: '',
      notes: '',
      lat: null,
      lng: null,
    });
    setEditingCustomer(null);
  };

  const handleEdit = async (customer) => {
    setEditingCustomer(customer);
    setFormData({
      person_type: customer.person_type || 'FISICA',
      name: customer.name || '',
      cpf: customer.cpf || '',
      rg: customer.rg || '',
      birth_date: customer.birth_date ? customer.birth_date.split('T')[0] : '',
      phone: customer.phone || '',
      email: customer.email || '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      delivery_fee: '',
      notes: customer.notes || '',
      lat: null,
      lng: null,
    });
    
    // Carregar endereço principal se existir
    if (customer.addresses && customer.addresses.length > 0) {
      const addr = customer.addresses[0];
      setFormData(prev => ({
        ...prev,
        cep: addr.cep || '',
        street: addr.street || '',
        number: addr.number || '',
        complement: addr.complement || '',
        neighborhood: addr.neighborhood || '',
        city: addr.city || '',
        state: addr.state || '',
        delivery_fee: addr.delivery_fee?.toString() || '',
        lat: addr.lat,
        lng: addr.lng,
      }));
    }
    
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar CEP obrigatório para novos clientes
    if (!editingCustomer) {
      if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
        toast.error('CEP é obrigatório para novos clientes');
        return;
      }
      if (!formData.street || !formData.number || !formData.neighborhood) {
        toast.error('Preencha o endereço completo (Rua, Número, Bairro)');
        return;
      }
    }
    
    try {
      const customerData = {
        person_type: formData.person_type,
        name: formData.name,
        cpf: formData.cpf || null,
        rg: formData.rg || null,
        birth_date: formData.birth_date || null,
        phone: formData.phone,
        email: formData.email || null,
        notes: formData.notes || null,
      };

      let customerId;
      
      if (editingCustomer) {
        await customers.update(editingCustomer.id, customerData);
        customerId = editingCustomer.id;
        toast.success('Cliente atualizado!');
      } else {
        const response = await customers.create(customerData);
        customerId = response.data.id;
        toast.success('Cliente cadastrado!');
      }
      
      // Criar ou atualizar endereço se preenchido
      if (formData.cep && formData.street) {
        // Auto-geocodificar se não tem coordenadas
        let lat = formData.lat;
        let lng = formData.lng;
        let fee = formData.delivery_fee ? parseFloat(formData.delivery_fee) : null;

        if (!lat || !lng) {
          const coords = await geocodeAddress(formData.street, formData.number || '1', formData.city, formData.state);
          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
            if (!fee) {
              const calc = calculateFeeFromCoords(lat, lng);
              fee = parseFloat(calc.fee);
            }
          }
        }

        const addressData = {
          label: 'Principal',
          cep: formData.cep,
          street: formData.street,
          number: formData.number,
          complement: formData.complement || null,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state,
          lat: lat,
          lng: lng,
          delivery_fee: fee,
        };
        
        if (editingCustomer && editingCustomer.addresses && editingCustomer.addresses.length > 0) {
          // Atualizar endereço existente
          await customers.updateAddress(customerId, editingCustomer.addresses[0].id, addressData);
        } else {
          // Criar novo endereço
          await customers.createAddress(customerId, addressData);
        }
      }
      
      setOpen(false);
      resetForm();
      loadCustomers();
    } catch (error) {
      toast.error(editingCustomer ? 'Erro ao atualizar cliente' : 'Erro ao cadastrar cliente');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;
    
    try {
      await customers.delete(customerId);
      toast.success('Cliente excluído!');
      loadCustomers();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleViewOrders = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await customers.orders(customer.id);
      setCustomerOrders(response.data);
      setShowOrdersModal(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleViewAddresses = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await customers.addresses(customer.id);
      setCustomerAddresses(response.data);
      setShowAddressesModal(true);
    } catch (error) {
      toast.error('Erro ao carregar endereços');
    }
  };

  const exportToCSV = () => {
    const headers = ['Nome', 'Tipo', 'CPF/CNPJ', 'Telefone', 'Email', 'Cidade', 'Estado'];
    const rows = filteredCustomers.map(c => [
      c.name,
      c.person_type === 'FISICA' ? 'Física' : 'Jurídica',
      c.cpf || '',
      c.phone,
      c.email || '',
      c.addresses?.[0]?.city || '',
      c.addresses?.[0]?.state || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `clientes_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Exportação concluída!');
  };

  const exportToPDF = () => {
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #333; border-bottom: 2px solid #FF6B6B; padding-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th { background: #FF6B6B; color: white; padding: 10px; text-align: left; }
    td { padding: 8px; border-bottom: 1px solid #ddd; }
    tr:nth-child(even) { background: #f9f9f9; }
    .footer { margin-top: 20px; text-align: center; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Relatório de Clientes - Hit Grill</h1>
  <p>Data: ${new Date().toLocaleDateString('pt-BR')} | Total: ${filteredCustomers.length} clientes</p>
  <table>
    <tr>
      <th>Nome</th>
      <th>Tipo</th>
      <th>Telefone</th>
      <th>Email</th>
      <th>Cidade/UF</th>
    </tr>
    ${filteredCustomers.map(c => `
      <tr>
        <td>${c.name}</td>
        <td>${c.person_type === 'FISICA' ? 'PF' : 'PJ'}</td>
        <td>${c.phone}</td>
        <td>${c.email || '-'}</td>
        <td>${c.addresses?.[0]?.city || '-'}/${c.addresses?.[0]?.state || '-'}</td>
      </tr>
    `).join('')}
  </table></div>
  <div class="footer">Gerado pelo Sistema Hit Grill</div>
</body>
</html>`;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF gerado!');
  };

  // Função para atualizar coordenadas em massa
  const handleBulkGeocoding = async () => {
    // Filtrar clientes que precisam de atualização (sem coordenadas ou sem CEP)
    const customersToUpdate = customersList.filter(c => {
      const addr = c.addresses?.[0];
      if (!addr) return false;
      // Atualizar se não tem coordenadas mas tem endereço
      return (!addr.lat || !addr.lng) && addr.street && addr.city;
    });

    if (customersToUpdate.length === 0) {
      toast.info('Todos os clientes já possuem coordenadas');
      return;
    }

    if (!window.confirm(`Deseja atualizar as coordenadas de ${customersToUpdate.length} cliente(s)?`)) {
      return;
    }

    setBulkUpdating(true);
    setBulkProgress({ current: 0, total: customersToUpdate.length });
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < customersToUpdate.length; i++) {
      const customer = customersToUpdate[i];
      const addr = customer.addresses[0];
      
      setBulkProgress({ current: i + 1, total: customersToUpdate.length });

      try {
        // Buscar coordenadas
        const coords = await geocodeAddress(addr.street, addr.number || '1', addr.city, addr.state);
        
        if (coords) {
          const { distance, fee } = calculateFeeFromCoords(coords.lat, coords.lng);
          
          // Atualizar via API (usando query params)
          const params = new URLSearchParams({
            lat: coords.lat.toString(),
            lng: coords.lng.toString(),
            delivery_fee: (addr.delivery_fee || parseFloat(fee)).toString()
          });
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customer.id}/addresses/${addr.id}/geocode?${params}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } else {
          errorCount++;
        }
        
        // Delay entre requisições para não sobrecarregar Nominatim
        await new Promise(resolve => setTimeout(resolve, 1200));
      } catch (e) {
        console.error('Erro ao atualizar cliente:', customer.name, e);
        errorCount++;
      }
    }

    setBulkUpdating(false);
    setBulkProgress({ current: 0, total: 0 });
    
    if (successCount > 0) {
      toast.success(`${successCount} cliente(s) atualizado(s) com sucesso!`);
      loadCustomers();
    }
    if (errorCount > 0) {
      toast.warning(`${errorCount} cliente(s) não puderam ser atualizados`);
    }
  };

  // Contadores de status de coordenadas
  const coordsStats = {
    withCoords: customersList.filter(c => c.addresses?.[0]?.lat && c.addresses?.[0]?.lng).length,
    withoutCoords: customersList.filter(c => c.addresses?.[0] && (!c.addresses[0].lat || !c.addresses[0].lng)).length,
    noAddress: customersList.filter(c => !c.addresses || c.addresses.length === 0).length,
  };

  const filteredCustomers = customersList.filter(c => {
    if (filterType !== 'TODOS' && c.person_type !== filterType) return false;
    return true;
  });

  if (loading && customersList.length === 0) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="customers-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Clientes
            </h1>
            <p className="text-slate-600">Gerencie sua base de clientes ({filteredCustomers.length} cadastrados)</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportToCSV} className="border-slate-300">
              <FileSpreadsheet size={18} className="mr-2" />
              Excel
            </Button>
            <Button variant="outline" onClick={exportToPDF} className="border-slate-300">
              <FileText size={18} className="mr-2" />
              PDF
            </Button>
            <Button 
              className="bg-[#FF6B6B] hover:bg-[#FF5252]" 
              onClick={() => { resetForm(); setOpen(true); }}
              data-testid="new-customer-btn"
            >
              <Plus size={20} className="mr-2" />
              Novo Cliente
            </Button>
          </div>
        </div>

        {/* Status de Coordenadas */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-emerald-400" />
                <span className="text-sm">Geolocalização</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {coordsStats.withCoords} com GPS
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  {coordsStats.withoutCoords} sem GPS
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-500" />
                  {coordsStats.noAddress} sem endereço
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkGeocoding}
              disabled={bulkUpdating || coordsStats.withoutCoords === 0}
              className="border-white/20 text-white hover:bg-white/10 hover:text-white"
              data-testid="bulk-geocode-btn"
            >
              {bulkUpdating ? (
                <>
                  <RefreshCw size={14} className="mr-1.5 animate-spin" />
                  {bulkProgress.current}/{bulkProgress.total}
                </>
              ) : (
                <>
                  <Navigation size={14} className="mr-1.5" />
                  Atualizar Coordenadas em Massa
                </>
              )}
            </Button>
          </div>
          {bulkUpdating && (
            <div className="mt-3">
              <div className="w-full bg-slate-600 rounded-full h-2">
                <div 
                  className="bg-emerald-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-300 mt-1.5">
                Processando cliente {bulkProgress.current} de {bulkProgress.total}... (aguarde ~1s por cliente)
              </p>
            </div>
          )}
        </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder="Buscar por nome, telefone, email ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-300"
                data-testid="customer-search"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48 bg-white border-slate-300">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos os tipos</SelectItem>
                <SelectItem value="FISICA">Pessoa Física</SelectItem>
                <SelectItem value="JURIDICA">Pessoa Jurídica</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabela de Clientes */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold">Cliente</TableHead>
                <TableHead className="font-bold">Contato</TableHead>
                <TableHead className="font-bold">Endereço</TableHead>
                <TableHead className="font-bold text-center">Pedidos</TableHead>
                <TableHead className="font-bold text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] flex items-center justify-center">
                          <User className="text-white" size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{customer.name}</p>
                          <p className="text-xs text-slate-500">
                            {customer.person_type === 'FISICA' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                            {customer.cpf && ` • ${customer.cpf}`}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="flex items-center gap-1 text-sm">
                          <Phone size={14} className="text-slate-400" />
                          {customer.phone}
                        </p>
                        {customer.email && (
                          <p className="flex items-center gap-1 text-sm text-slate-500">
                            <Mail size={14} className="text-slate-400" />
                            {customer.email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.addresses && customer.addresses.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-1.5">
                            {customer.addresses[0].lat && customer.addresses[0].lng ? (
                              <span className="w-2 h-2 rounded-full bg-emerald-500" title={`GPS: ${customer.addresses[0].lat?.toFixed(4)}, ${customer.addresses[0].lng?.toFixed(4)}`} />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-amber-400" title="Sem coordenadas GPS" />
                            )}
                            <p className="text-sm font-medium">{customer.addresses[0].street}, {customer.addresses[0].number}</p>
                          </div>
                          <p className="text-xs text-slate-500 ml-3.5">
                            {customer.addresses[0].neighborhood && `${customer.addresses[0].neighborhood} • `}
                            {customer.addresses[0].city}/{customer.addresses[0].state || 'SP'}
                            {customer.addresses[0].cep && <span className="text-slate-400"> • CEP: {customer.addresses[0].cep}</span>}
                          </p>
                          {customer.addresses.length > 1 && (
                            <button 
                              onClick={() => handleViewAddresses(customer)}
                              className="ml-3.5 text-xs text-[#FF6B6B] hover:underline"
                            >
                              +{customer.addresses.length - 1} endereço(s)
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">Não cadastrado</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewOrders(customer)}
                        className="text-slate-600 hover:text-[#FF6B6B]"
                      >
                        <History size={16} className="mr-1" />
                        Ver histórico
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewAddresses(customer)}
                          title="Ver endereços"
                          className="hover:bg-blue-50 hover:text-blue-600"
                        >
                          <MapPin size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(customer)}
                          title="Editar"
                          className="hover:bg-slate-100 hover:text-slate-700"
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(customer.id)}
                          title="Excluir"
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    {searchTerm ? 'Nenhum cliente encontrado com esses critérios' : 'Nenhum cliente cadastrado'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Modal de Cadastro/Edição */}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tipo de Pessoa */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Tipo de Pessoa</Label>
                <RadioGroup value={formData.person_type} onValueChange={(value) => setFormData({ ...formData, person_type: value })}>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="FISICA" id="fisica" />
                      <Label htmlFor="fisica" className="cursor-pointer">Pessoa Física</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="JURIDICA" id="juridica" />
                      <Label htmlFor="juridica" className="cursor-pointer">Pessoa Jurídica</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Nome */}
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-white border-slate-300"
                />
              </div>

              {/* CPF, RG, Nascimento */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="cpf">{formData.person_type === 'FISICA' ? 'CPF' : 'CNPJ'}</Label>
                  <Input
                    id="cpf"
                    data-testid="customer-cpf-input"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="bg-white border-slate-300"
                  />
                </div>
                <div>
                  <Label htmlFor="rg">{formData.person_type === 'FISICA' ? 'RG' : 'IE'}</Label>
                  <Input
                    id="rg"
                    value={formData.rg}
                    onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                    className="bg-white border-slate-300"
                  />
                </div>
                <div>
                  <Label htmlFor="birth_date">{formData.person_type === 'FISICA' ? 'Nascimento' : 'Fundação'}</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="bg-white border-slate-300"
                  />
                </div>
              </div>

              {/* Contato Principal */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Contato Principal</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input
                      id="phone"
                      data-testid="customer-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      data-testid="customer-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço Principal */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">
                  Endereço Principal {!editingCustomer && <span className="text-red-500">*</span>}
                </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <Label htmlFor="cep">CEP *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="cep"
                            value={formData.cep}
                            onChange={(e) => setFormData({ ...formData, cep: e.target.value.replace(/\D/g, '') })}
                            maxLength={8}
                            required
                            className="bg-white border-slate-300"
                          />
                          <Button type="button" size="icon" variant="outline" onClick={handleCepSearch} className="border-slate-300">
                            <Search size={16} />
                          </Button>
                        </div>
                      </div>
                      <div className="col-span-7">
                        <Label htmlFor="street">Endereço *</Label>
                        <Input
                          id="street"
                          value={formData.street}
                          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                          required
                          className="bg-white border-slate-300"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="number">Núm. *</Label>
                        <Input
                          id="number"
                          value={formData.number}
                          onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                          required
                          className="bg-white border-slate-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="neighborhood">Bairro *</Label>
                        <Input
                          id="neighborhood"
                          value={formData.neighborhood}
                          onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                          required
                          className="bg-white border-slate-300"
                        />
                      </div>
                      <div>
                        <Label htmlFor="complement">Complemento</Label>
                        <Input
                          id="complement"
                          value={formData.complement}
                          onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                          className="bg-white border-slate-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="city">Município</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="bg-white border-slate-300"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">Estado</Label>
                        <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                          <SelectTrigger className="bg-white border-slate-300">
                            <SelectValue placeholder="UF" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATES.map(uf => (
                              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Geolocalização */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <MapPin size={16} className="text-[#FF6B6B]" />
                        Geolocalização (Google Maps)
                      </h4>
                      <div className="flex gap-2 mb-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleUpdateCoordinates}
                          className="border-slate-300 text-sm"
                          size="sm"
                        >
                          <RefreshCw size={14} className="mr-1.5" />
                          Buscar Coordenadas
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCalculateDistance}
                          className="border-slate-300 text-sm"
                          size="sm"
                        >
                          <Navigation size={14} className="mr-1.5" />
                          Calcular Taxa
                        </Button>
                      </div>
                      {formData.lat && formData.lng ? (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          <span className="text-emerald-700 font-medium">
                            {formData.lat.toFixed(5)}, {formData.lng.toFixed(5)}
                          </span>
                          {formData.delivery_fee && (
                            <span className="text-slate-500 ml-2">
                              | Taxa: R$ {formData.delivery_fee}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                          <div className="w-2 h-2 rounded-full bg-amber-400" />
                          Sem coordenadas — busque pelo CEP ou clique em "Buscar Coordenadas"
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-2">
                        Coordenadas auto-preenchidas ao buscar CEP. Necessárias para roteirização de entregas.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="delivery_fee">Taxa de Entrega (R$)</Label>
                      <Input
                        id="delivery_fee"
                        type="number"
                        step="0.01"
                        value={formData.delivery_fee}
                        onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                        placeholder="Ex: 5.00"
                        className="bg-white border-slate-300"
                      />
                    </div>
                  </div>
                </div>

              {/* Observações */}
              <div>
                <Label htmlFor="notes">Observações</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="bg-white border-slate-300"
                />
              </div>

              <Button type="submit" className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] text-lg py-6" data-testid="save-customer-btn">
                {editingCustomer ? 'ATUALIZAR' : 'SALVAR'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal de Histórico de Pedidos */}
        <Dialog open={showOrdersModal} onOpenChange={setShowOrdersModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
                Histórico de Pedidos - {selectedCustomer?.name}
              </DialogTitle>
            </DialogHeader>
            {customerOrders.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Pedido</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerOrders.map(order => (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-sm">#{order.id.substring(0, 8)}</TableCell>
                      <TableCell>{formatDate(order.created_at)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          order.origin === 'DELIVERY' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {order.origin}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          order.status === 'ENTREGUE' ? 'bg-green-100 text-green-700' :
                          order.status === 'CANCELADO' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {order.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(order.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                Nenhum pedido encontrado para este cliente
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Endereços */}
        <Dialog open={showAddressesModal} onOpenChange={setShowAddressesModal}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
                Endereços - {selectedCustomer?.name}
              </DialogTitle>
            </DialogHeader>
            {customerAddresses.length > 0 ? (
              <div className="space-y-4">
                {customerAddresses.map((addr, idx) => (
                  <div key={addr.id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-semibold text-[#FF6B6B]">{addr.label}</span>
                        {addr.is_default && (
                          <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">Principal</span>
                        )}
                      </div>
                      {addr.delivery_fee && (
                        <span className="text-sm text-slate-600">Taxa: {formatCurrency(addr.delivery_fee)}</span>
                      )}
                    </div>
                    <p className="mt-2 text-slate-700">
                      {addr.street}, {addr.number}
                      {addr.complement && ` - ${addr.complement}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {addr.neighborhood} • {addr.city}/{addr.state} • CEP: {addr.cep}
                    </p>
                    {addr.lat && addr.lng && (
                      <p className="text-xs text-slate-400 mt-1">
                        📍 {addr.lat.toFixed(4)}, {addr.lng.toFixed(4)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                Nenhum endereço cadastrado
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
// Updated Mon Feb 16 22:20:02 UTC 2026
// v2 Mon Feb 16 22:24:38 UTC 2026
